import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { StorageService } from '../shared/storage.service';
import { ApiRequestService } from '../shared/api-request.service';
import { UtilsService } from '../shared/utils.service';
import { AppService } from '../app.service';

/**
 * Public page reached from the partner welcome email (set-password/:token). The customer chooses a
 * password, which also confirms their email address, and is then signed in.
 */
@Component({
  selector: 'app-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SetPasswordComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private storage: StorageService = inject(StorageService);
  private api: ApiRequestService = inject(ApiRequestService);
  utils: UtilsService = inject(UtilsService);
  private app: AppService = inject(AppService);

  token: string = '';

  password: string = '';
  passwordConfirmation: string = '';
  passwordValidated: boolean = false;
  passwordFieldType: string = 'password';
  confirmPasswordFieldType: string = 'password';

  // The page state: validating the token, showing the form, or showing the unusable link warning.
  state: 'validating' | 'form' | 'invalid' | 'error' = 'validating';

  // The API message and reason for an unusable link (expired, used or unknown).
  invalidMessage: string = '';
  invalidReason: string = '';

  inProgress: boolean = false;

  ngOnInit(): void {
    this.token = this.route.snapshot.params['token'];

    // Validate the set-password token before showing the form.
    this.api
      .makeRequest('post', 'v2/verify-email/set-password/validate', {
        token: this.token
      })
      .then((): void => {
        this.state = 'form';
      })
      .catch((errorResponse: any): void => {
        if ( !this.showInvalidState(errorResponse) ) {
          this.state = 'error';
          this.utils.handleAPIErrors(errorResponse);
        }
      });
  }

  /**
   * Switch to the warning state when the API reports the token is not usable (valid === false).
   *
   * @param errorResponse The API error response.
   * @returns {boolean} True when the warning state is shown.
   */
  private showInvalidState(errorResponse: any): boolean {
    if ( errorResponse?.error?.valid !== false ) {
      return false;
    }
    this.invalidMessage = errorResponse.error.message || 'This link can no longer be used.';
    this.invalidReason = errorResponse.error.reason || '';
    this.state = 'invalid';
    return true;
  }

  /**
   * Whether the warning state offers the Forgot Password link (expired or used links).
   */
  get canResetPassword(): boolean {
    return this.invalidReason === 'expired' || this.invalidReason === 'used';
  }

  onSetPassword(form: NgForm): void {
    if ( !form.valid ) {
      this.utils.showModal(
        'Error Message',
        'Please make sure you enter all required fields with valid formats.'
      );
      return;
    }
    if ( this.inProgress ) {
      return;
    }
    this.inProgress = true;

    this.api
      .makeRequest('post', 'v2/verify-email/set-password', {
        token: this.token,
        password: this.password,
        password_confirmation: this.passwordConfirmation
      })
      .then((response: any): void => {
        this.handleSignInResponse(response);
      })
      .catch((errorResponse: any): void => {
        // A token that stopped being usable (expired, used, unknown, or a concurrent submit) shows the
        // warning state. Anything else (for example a password validation error) keeps the form.
        if ( !this.showInvalidState(errorResponse) ) {
          this.utils.handleAPIErrors(errorResponse);
        }
      })
      .finally((): void => {
        this.inProgress = false;
      });
  }

  // Copied from ResetPasswordComponent (similar methods in signin.component.ts, change-password.component.ts).
  private handleSignInResponse(response: any): void {
    if (
      !response.account ||
      !response.user ||
      !response.user_account_link ||
      !response.api_token
    ) {
      this.utils.showToast(
        'We could not refresh your account details. You may need to log in again.'
      );
      return;
    }

    // user is authenticated
    this.app.isAuthenticated = true;
    this.storage.set('AuthToken', response.api_token);
    this.app.user.apply(response.user);
    this.app.account.apply(response.account);
    this.app.userAccountLink.apply(response.user_account_link);

    this.utils.redirectPage(['/dashboard']);
  }

  getOutputPasswordValidated($event: boolean): void {
    this.passwordValidated = $event;
  }

  /**
   * Toggles the visibility of the password field based on the given field parameter.
   *
   * @param {string} field - The name of the field to toggle visibility for ('password' or 'confirmPassword').
   * @returns {void}
   */
  togglePasswordVisibility(field: string): void {
    if ( field === 'password' ) {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    } else if ( field === 'confirmPassword' ) {
      this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
    }
  }
}
